import { useEffect, useState } from 'react';
import { useAdminData } from '../../admin/AdminDataContext';
import { useMapContext } from '../../map/MapContext';
import { searchApi, type AddressHit, type SearchHit } from '../../lib/searchApi';

type CombinedHit = { kind: 'object'; hit: SearchHit } | { kind: 'address'; hit: AddressHit };

function hitKey(combined: CombinedHit): string {
  return combined.kind === 'object' ? `object_${combined.hit.feature_id}` : `address_${combined.hit.osm_id}`;
}

function hitTitle(combined: CombinedHit): string {
  if (combined.kind === 'address') return combined.hit.display_address;
  const firstString = Object.values(combined.hit.attributes).find((v): v is string => typeof v === 'string' && v.length > 0);
  return firstString ?? combined.hit.layer_name;
}

/** "Название слоя" for an object hit, literally "Адрес" for an address one —
 * this is the whole point of merging the two kinds into one list instead of
 * two separate ones: the badge is what tells them apart. */
function hitBadge(combined: CombinedHit): string {
  if (combined.kind === 'object') return combined.hit.layer_name;
  return combined.hit.city ? `Адрес · ${combined.hit.city}` : 'Адрес';
}

export function MapSearch() {
  const { flyToSearchHit, getViewState } = useMapContext();
  const { mapSettings } = useAdminData();
  const search = mapSettings?.search;
  const minLength = search?.minQueryLength ?? 3;
  const maxResults = search?.maxResults ?? 20;
  const flyToOnSelect = search?.flyToOnSelect ?? true;
  const includeObjects = search?.searchObjects ?? true;
  const includeAddresses = search?.searchAddresses ?? false;
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [results, setResults] = useState<CombinedHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const queryLongEnough = query.trim().length >= minLength;

  useEffect(() => {
    const q = query.trim();
    if (q.length < minLength) {
      setResults([]);
      setSearchError(null);
      return;
    }
    setSearching(true);
    const timeout = setTimeout(() => {
      const view = getViewState();
      const near = view ? { lat: view.lat, lon: view.lon } : undefined;
      Promise.all([
        includeObjects ? searchApi.searchObjects(q, maxResults) : Promise.resolve(null),
        includeAddresses ? searchApi.searchAddresses(q, maxResults, near) : Promise.resolve(null),
      ])
        .then(([objects, addresses]) => {
          const combined: CombinedHit[] = [
            ...(objects?.hits.map((hit): CombinedHit => ({ kind: 'object', hit })) ?? []),
            ...(addresses?.hits.map((hit): CombinedHit => ({ kind: 'address', hit })) ?? []),
          ];
          combined.sort((a, b) => b.hit.score - a.hit.score);
          setResults(combined.slice(0, maxResults));
          setSearchError(null);
        })
        .catch(() => setSearchError('Не удалось выполнить поиск'))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, minLength, maxResults, includeObjects, includeAddresses]);

  const placeholder =
    includeObjects && includeAddresses
      ? 'Найти объект или адрес…'
      : includeAddresses
        ? 'Найти адрес…'
        : includeObjects
          ? 'Найти объект по атрибутам слоя…'
          : 'Поиск отключён в настройках карты';

  const handlePick = (combined: CombinedHit) => {
    if (combined.hit.geometry && flyToOnSelect) {
      if (combined.kind === 'object') {
        flyToSearchHit(combined.hit.geometry, { name: hitTitle(combined), objectType: combined.hit.layer_name, ...combined.hit.attributes });
      } else {
        flyToSearchHit(combined.hit.geometry, { name: combined.hit.display_address });
      }
    }
    setQuery(hitTitle(combined));
    setFocused(false);
  };

  return (
    // Clamped narrower than "the full viewport minus margins" on purpose — this
    // bar is centered between the layers-drawer and AI-panel corner buttons
    // (both size-11 at top-6), so it needs room to not run under them on a
    // phone-width screen, not just avoid overflowing the viewport itself.
    // z-20 — the results dropdown below can grow to max-h-72 and reach well
    // past MapControls' top-[84px] toolbar; at equal z-index MapControls
    // (mounted after this) would paint over the dropdown wherever they overlap.
    <div className="absolute left-1/2 top-6 z-20 w-[min(420px,calc(100vw-140px))] -translate-x-1/2">
      <div className="flex items-center gap-2 rounded-xl border border-border-default bg-bg-surface px-4 py-2.5 shadow-sm">
        <span className="text-text-secondary">🔍</span>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 120)}
          placeholder={placeholder}
          disabled={!includeObjects && !includeAddresses}
          className="flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-secondary"
        />
        {query && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setQuery('')}
            className="text-text-secondary"
            title="Очистить"
          >
            ✕
          </button>
        )}
      </div>

      {focused && query.trim() && (
        <div className="mt-2 max-h-72 overflow-y-auto rounded-xl border border-border-default bg-bg-surface p-2 shadow-lg">
          {!queryLongEnough ? (
            <p className="px-3 py-2 text-xs text-text-secondary">
              Введите ещё {minLength - query.trim().length} симв. для поиска
            </p>
          ) : searching ? (
            <p className="px-3 py-2 text-xs text-text-secondary">Ищем…</p>
          ) : searchError ? (
            <p className="px-3 py-2 text-xs font-medium text-status-danger">{searchError}</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-2 text-xs text-text-secondary">Ничего не найдено</p>
          ) : (
            results.map((combined) => (
              <button
                key={hitKey(combined)}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handlePick(combined)}
                className="flex w-full flex-col items-start rounded-lg px-3 py-2 text-left hover:bg-bg-surface-secondary"
              >
                <span className="text-sm font-medium text-text-primary">{hitTitle(combined)}</span>
                <span className="text-[11px] text-text-secondary">{hitBadge(combined)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
